import React, { useState, useMemo } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck, faClock } from '@fortawesome/free-solid-svg-icons'
import axios from 'axios'
import { NETLIFY_FUNCTIONS_PATH } from '../../../utils'
import { useScrollToAnchor } from '../../../hooks/useScrollToAnchor.ts'
import { defaultAttributionOptions, shuffleArray } from './attribution-survey'

const SuccessInfoCard = () => (
    <div
        className="info-card milan-bg-white mb-3 p-3"
        style={{ borderRadius: '16px', border: '1px solid #e3f2f2' }}
    >
        <div className="info-icon mb-3" style={{ textAlign: 'center' }}>
            <FontAwesomeIcon
                icon={faClock}
                style={{
                    color: '#00a5b4',
                    fontSize: '2rem',
                    opacity: 0.8,
                }}
            />
        </div>
        <p
            style={{
                textAlign: 'center',
                fontSize: '1rem',
                color: '#555',
                lineHeight: '1.6',
                margin: 0,
            }}
        >
            We will be contacting you shortly with more information.
            <br />
            <strong style={{ color: '#00a5b4' }}>
                During normal business hours, expect to hear from us in about 15
                minutes.
            </strong>
        </p>
    </div>
)

/**
 * AttributionSurvey component for collecting "How did you hear about us?" data
 * @param {object} formValues - User's inputs for personalization
 * @param {function} onComplete - Callback function when survey is completed
 * @param {string} apiEndpoint - API endpoint for survey submission (default: '/.netlify/functions/attribution-survey')
 * @param {array} customOptions - Custom attribution options (optional, uses default if not provided)
 * @param {string} primaryColor - Primary color for selected state (default: '#00a5b4')
 * @param {function} InfoCard - Custom info card component (optional, defaults to SuccessInfoCard)
 * @param {boolean} isFullWidth - Whether to use full width for options (default
 */
export function AttributionSurvey({
    formValues,
    onComplete,
    apiEndpoint = `${NETLIFY_FUNCTIONS_PATH}attribution-survey-handler`,
    customOptions,
    primaryColor = '#00a5b4',
    InfoCard = SuccessInfoCard,
    isFullWidth = true,
}) {
    const [selectedOption, setSelectedOption] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const { scrollToAnchor } = useScrollToAnchor({ offset: 300 })

    // Randomize options while keeping "Other", "Prefer not to answer", and "Social channels" at the end
    const attributionOptions = useMemo(() => {
        const options = customOptions || defaultAttributionOptions

        // Separate special options from other options
        const other = options.find(opt => opt.id === 'other')
        const preferNotToAnswer = options.find(
            opt => opt.id === 'prefer_not_answer'
        )
        //   const socialChannels = options.find((opt) => opt.id === 'social_channels');

        // Get all other options
        const otherOptions = options.filter(
            opt => opt.id !== 'other' && opt.id !== 'prefer_not_answer'
        )

        // Shuffle the other options
        const shuffledOptions = shuffleArray(otherOptions)

        // Add the special options at the end (only if they exist)
        //   if (socialChannels) shuffledOptions.push(socialChannels);
        if (other) shuffledOptions.push(other)
        if (preferNotToAnswer) shuffledOptions.push(preferNotToAnswer)

        return shuffledOptions
    }, [customOptions])

    const handleSubmit = async optionValue => {
        setIsSubmitting(true)

        try {
            const formData = {
                ...formValues,
                attribution: optionValue || selectedOption,
            }
            const response = await axios.post(apiEndpoint, formData)

            // log success response
            console.log(response.data)
        } catch (error) {
            // Log error but don't block the user from proceeding
            console.error('Attribution survey submission error:', error)
        }

        // Always proceed to next step regardless of survey submission result
        setTimeout(() => {
            onComplete(optionValue || selectedOption)
        }, 300)
    }

    const handleOptionSelect = option => {
        setSelectedOption(option)
        scrollToAnchor('attribution-survey')
        // Auto-submit when option is selected
        setTimeout(() => {
            handleSubmit(option)
        }, 400)
    }

    return (
        <div id="attribution-survey" className="attribution-survey px-3 my-4">
            <div className="row justify-content-center mb-3">
                <div className={`col-12 ${isFullWidth ? '' : 'col-xl-10'}`}>
                    <InfoCard />
                </div>
            </div>
            <h4
                className="text-center mb-4"
                style={{
                    color: primaryColor,
                    fontWeight: '600',
                }}
            >
                How did you hear about us
                {formValues.first_name ? `, ${formValues.first_name}` : ''}?
            </h4>

            <div className="row justify-content-center">
                <div className={`col-12 ${isFullWidth ? '' : 'col-xl-10'}`}>
                    <div className="row">
                        {attributionOptions.map((option, index) => (
                            <div
                                key={index}
                                className={`col-12 ${
                                    option.fullWidth ? '' : 'col-md-6'
                                } mb-2`}
                            >
                                <button
                                    type="button"
                                    className="attribution-option-btn"
                                    onClick={() =>
                                        handleOptionSelect(option.value)
                                    }
                                    disabled={isSubmitting}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        width: '100%',
                                        padding: '10px 14px',
                                        border:
                                            selectedOption === option.value
                                                ? `2px solid ${primaryColor}`
                                                : '2px solid #e9ecef',
                                        borderRadius: '12px',
                                        backgroundColor:
                                            selectedOption === option.value
                                                ? '#f0fafb'
                                                : '#fff',
                                        cursor: isSubmitting
                                            ? 'not-allowed'
                                            : 'pointer',
                                        transition: 'all 0.3s ease',
                                        boxShadow:
                                            selectedOption === option.value
                                                ? `0 2px 8px ${primaryColor}15`
                                                : 'none',
                                        opacity:
                                            isSubmitting &&
                                            selectedOption !== option.value
                                                ? 0.6
                                                : 1,
                                        transform:
                                            selectedOption === option.value
                                                ? 'scale(1.02)'
                                                : 'scale(1)',
                                        textAlign: 'left',
                                        fontSize: '1rem',
                                        fontWeight:
                                            selectedOption === option.value
                                                ? '500'
                                                : '400',
                                        color:
                                            selectedOption === option.value
                                                ? primaryColor
                                                : '#333',
                                        height: '100%',
                                        minHeight: '50px',
                                    }}
                                    onMouseEnter={e => {
                                        if (
                                            !isSubmitting &&
                                            selectedOption !== option.value
                                        ) {
                                            e.currentTarget.style.borderColor =
                                                '#90d5d5'
                                            e.currentTarget.style.backgroundColor =
                                                '#fafafa'
                                        }
                                    }}
                                    onMouseLeave={e => {
                                        if (
                                            !isSubmitting &&
                                            selectedOption !== option.value
                                        ) {
                                            e.currentTarget.style.borderColor =
                                                '#e9ecef'
                                            e.currentTarget.style.backgroundColor =
                                                '#fff'
                                        }
                                    }}
                                >
                                    <span
                                        style={{
                                            fontSize: '1.2rem',
                                            marginRight: '4px',
                                            minWidth: '30px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '5px',
                                        }}
                                    >
                                        <FontAwesomeIcon
                                            icon={option.icon}
                                            style={{
                                                color:
                                                    selectedOption ===
                                                    option.value
                                                        ? primaryColor
                                                        : option.iconColor ||
                                                          '#666',
                                                transition: 'color 0.3s ease',
                                            }}
                                        />
                                        {option.iconSecondary && (
                                            <>
                                                <span
                                                    style={{
                                                        fontSize: '1rem',
                                                        color: '#ddd',
                                                    }}
                                                >
                                                    /
                                                </span>
                                                <FontAwesomeIcon
                                                    icon={option.iconSecondary}
                                                    style={{
                                                        color:
                                                            selectedOption ===
                                                            option.value
                                                                ? primaryColor
                                                                : option.iconSecondaryColor ||
                                                                  '#666',
                                                        transition:
                                                            'color 0.3s ease',
                                                        marginRight: '8px',
                                                    }}
                                                />
                                            </>
                                        )}
                                        {/* {option.iconTertiary && (
                                            <>
                                                <span style={{ fontSize: '1rem', color: '#ddd' }}>/</span>
                                                <FontAwesomeIcon
                                                    icon={option.iconTertiary}
                                                    style={{
                                                        fontSize: '1.2rem',
                                                        color:
                                                            selectedOption === option.value
                                                                ? primaryColor
                                                                : option.iconTertiaryColor || '#666',
                                                        transition: 'color 0.3s ease',
                                                    }}
                                                />
                                            </>
                                        )} */}
                                    </span>
                                    <span style={{ flex: 1 }}>
                                        {option.label}
                                    </span>
                                    {selectedOption === option.value && (
                                        <FontAwesomeIcon
                                            icon={faCheck}
                                            style={{
                                                color: primaryColor,
                                                marginLeft: '10px',
                                            }}
                                        />
                                    )}
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default AttributionSurvey
